import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    doFilterBranches,
    fetchBranches,
    fetchDoctors,
    fetchServices,
    fetchServicesTimeslots,
    fetchServicesTimetable,
    selectBranch,
    selectDoctor,
    setBranchAutoSelect,
    setDoctorAutoSelect,
    setPreselectedData,
} from './store/serviceSlice';
import { useWidgetProps } from '../WidgetPropsContext';
import {
    getDoctors,
    getPreselectedData,
    getSelectedDate,
    getSelectedService,
    getServiceFilter,
    getServiceSelected,
    getTimeslots,
    isBranchAutoSelect,
    isDoctorAutoSelect,
} from './store/selectors';
import {
    convertTimeZone, formatFullDate, getEndDate, SERVER_TIMEZONE,
} from '../utils';
import api from './ServiceBookingForm/services/api';
import { debugLog } from './util/debug';

let baseUrl = '';

export const getTimetableInfo = async (doctor, branchId, doctorId) => {
    const dateStart = formatFullDate(convertTimeZone(new Date(Date.now()), null, SERVER_TIMEZONE));
    const endDate = getEndDate(new Date(Date.now()));
    const dateEnd = formatFullDate(endDate);
    const doctorAssignments = doctor?.assignments ?? [];
    if (!doctorAssignments.length) {
        return;
    }
    let assignment = doctorAssignments[0];
    if (doctorAssignments.length > 1) {
        const a = await api.getAssignmentsInfo(baseUrl, doctorAssignments);
        const found = a?.data?.find(item => item?.branchId === branchId);
        if (found) {
            assignment = found?.assignmentId;
        }
    }
    // eslint-disable-next-line consistent-return
    return {
        assignment,
        doctorId,
        dateStart,
        dateEnd,
    };
};

export const getTimetableInfoSafe = async (doctor, branchId, doctorId) => {
    if (!doctor?.branches || !doctor.branches.includes(branchId)) {
        return null;
    }
    const dateStart = formatFullDate(convertTimeZone(new Date(Date.now()), null, SERVER_TIMEZONE));
    const endDate = getEndDate(new Date(Date.now()));
    const dateEnd = formatFullDate(endDate);
    const doctorAssignments = doctor?.assignments ?? [];
    if (!doctorAssignments.length) {
        return;
    }
    let assignment = doctorAssignments[0];
    if (doctorAssignments.length > 1) {
        const a = await api.getAssignmentsInfo(baseUrl, doctorAssignments);
        const found = a?.data?.find(item => item?.branchId === branchId);
        if (found) {
            assignment = found?.assignmentId;
        }
    }
    // eslint-disable-next-line consistent-return
    return {
        assignment,
        doctorId,
        dateStart,
        dateEnd,
    };
};

export const ServiceBookingContainer = ({ skipInitialDataLoad, skipTimetableAutoLoad }) => {
    const dispatch = useDispatch();

    const selected = useSelector(getServiceSelected);
    const date = useSelector(getSelectedDate);
    const doctors = useSelector(getDoctors);
    const {
        baseUrl: baseUrlWidget,
        currentBranchId,
        // isClosed,
    } = useWidgetProps();
    baseUrl = baseUrlWidget;
    const filter = useSelector(getServiceFilter);
    const timeslots = useSelector(getTimeslots);
    const doctorAutoSelect = useSelector(isDoctorAutoSelect);
    const branchAutoSelect = useSelector(isBranchAutoSelect);
    const { isLoading } = useSelector(getSelectedService);
    const preselectedData = useSelector(getPreselectedData);

    useEffect(() => {
        debugLog('1 effect selected?.branches, selected?.services, date, branchAutoSelect ', [ selected?.branches, selected?.services, date, branchAutoSelect ]);
        // debugLog('useEffect [ selected?.branches, selected?.services, date ]', {
        //     selectedBranch: selected?.branches,
        //     selectedService: selected?.services,
        //     date,
        //     filter,
        //     branchAutoSelect,
        // });

        if (preselectedData?.reloadData) {
            debugLog('1 preselect data reload. skip', preselectedData);
            return;
        }

        if (!selected?.branches && selected?.services && (branchAutoSelect)) {
            if (filter?.branches?.length === 1) {
                debugLog('1 auto selectBranch', filter?.branches?.[0]?.id);
                dispatch(selectBranch(filter?.branches?.[0]?.id));
            } else {
                debugLog('1 fetchBranches auto select NOT one branch');
            }
        } else {
            debugLog('1 skip selectBranch', {
                selected,
                filter,
                branchAutoSelect,
            });
        }
        if (selected.services && selected.branches && (!selected?.doctors)) {
            const request = {
                baseUrl,
                date,
                serviceId: selected.services,
                branchId: selected.branches,
            };
            debugLog('1 fetchServicesTimeslots', request);
            dispatch(fetchServicesTimeslots(request));
        }
    }, [ selected?.branches, selected?.services, date, branchAutoSelect, preselectedData?.reloadData ]);

    useEffect(() => {
        debugLog('2 useEffect [ timeslots, selected?.doctors, doctorAutoSelect ]', {
            timeslots,
            selected,
            doctorAutoSelect,
            selectedBranches: selected?.branches,
        });
        if (preselectedData?.reloadData) {
            debugLog('2 preselect data reload. skip', preselectedData);
            return;
        }
        if (!selected?.doctors && timeslots?.length > 0 && (doctorAutoSelect) && !isLoading && selected?.branches) {
            dispatch(doFilterBranches());
            debugLog(`selectDoctor isLoading = ${isLoading} doctorForSelect = ${selected?.doctors}`, timeslots?.[0]?.doctorId, timeslots);
            if (timeslots?.length === 1 && timeslots?.[0]?.branches?.includes?.(selected?.branches)) {
                debugLog('2 auto selectDoctor', timeslots?.[0]?.doctorId);
                dispatch(selectDoctor(timeslots?.[0]?.doctorId));
            } else {
                debugLog('2 skip auto selectDoctor', 'timeslots?.length !== 1');
            }
        } else {
            debugLog('2 skip selectDoctor', {
                selected,
                timeslots,
                doctorAutoSelect,
                isLoading,
            });
        }
    }, [ timeslots, selected?.doctors, doctorAutoSelect, isLoading, selected?.branches, preselectedData?.reloadData ]);

    const loadTimetable = async (doc) => {
        const info = await getTimetableInfo(doc, selected.branches, selected?.doctors);
        debugLog('Info for timetable', info);
        if (info?.assignment) {
            dispatch(fetchServicesTimetable({
                baseUrl,
                ...info,
            }));
        } else {
            debugLog('Assignment for timetable not found', info);
        }
    };

    useEffect(() => {
        // if (isClosed) {
        //     return;
        // }
        debugLog('3 useEffect [selected?.doctors]', selected?.doctors);
        if (preselectedData?.reloadData) {
            debugLog('3 preselect data reload. skip', preselectedData);
            return;
        }
        if (!skipTimetableAutoLoad) {
            const doc = doctors?.find(({ doctorId }) => doctorId === selected?.doctors);
            if (!doc) {
                debugLog('3 doctor not found', selected?.doctors, doctors);
                return;
            }
            debugLog('3 loadTimetable', doc);
            loadTimetable(doc);
        }
    }, [ selected?.doctors, preselectedData?.reloadData ]);

    useEffect(() => {
        // if (isClosed) {
        //     return;
        // }
        if ((currentBranchId || currentBranchId === null) && !skipInitialDataLoad) {
            dispatch(fetchBranches({ baseUrl, currentBranchId }));
            dispatch(fetchServices({ baseUrl }));
            dispatch(fetchDoctors({ baseUrl, currentBranchId }));
            dispatch(setBranchAutoSelect(true));
            dispatch(setDoctorAutoSelect(true));
            debugLog('useEffect 0 [ baseUrl ]', baseUrl);
        }
    }, [currentBranchId]);

    useEffect(() => {
        debugLog('4 useEffect [ preselectedData?.doctors, preselectedData?.services, preselectedData?.branches, preselectedData?.reloadData, preselectedData?.timeslotsLoaded, preselectedData?.timetableLoaded ]', { preselectedData });
        if (!preselectedData?.doctors && !preselectedData?.services && !preselectedData?.branches) {
            debugLog('4 useEffect  skip', preselectedData);
            return;
        }
        if (preselectedData?.reloadData) {
            if (!preselectedData?.timeslotsLoaded
            // && !preselectedData?.timetableLoaded
            ) {
                debugLog('4 preselect data reload. Starting', preselectedData);
                // dispatch(setPreselectedData({ reloadData: false }));
                setTimeout(() => {
                    const request = {
                        baseUrl,
                        date,
                        serviceId: preselectedData?.services ?? selected.services,
                        branchId: preselectedData?.branches ?? selected.branches,
                    };
                    dispatch(fetchServicesTimeslots(request));
                }, 0);
            } else if (preselectedData?.timeslotsLoaded) {
                debugLog('4 preselect data reload. Finished', preselectedData);
                dispatch(setPreselectedData({ reloadData: false }));
            }
        } else {
            debugLog('4 skip preselect data', preselectedData);
        }
    }, [ preselectedData?.doctors, preselectedData?.services, preselectedData?.branches, preselectedData?.reloadData, preselectedData?.timeslotsLoaded, preselectedData?.timetableLoaded ]);

    return <>
        {/* <Log value={service}/> */}
    </>;
};
